import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Link,
  Button,
} from '@mui/material';
import {
  School,
  Groups,
  Science,
  Computer,
  Storage,
  Psychology,
  ExpandMore as ExpandMoreIcon,
  Description,
} from '@mui/icons-material';

const Academics = () => {
  const programs = [
    {
      title: 'Bachelor of Engineering (B.E.)',
      icon: <Computer sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Established in 1992, the Department of Computer Science & Engineering at MBM University was one of the earliest institutes in the country to provide education in computing. Starting with an intake of 33 students, the department has grown significantly over the years.',
      specializations: [
        {
          name: 'Computer Science Engineering',
          description: 'Comprehensive program focusing on computer science fundamentals, software development, and system design.',
          features: [
            'Core programming and algorithms',
            'Software engineering principles',
            'Database management systems',
            'Computer networks and security',
            'Web and mobile development',
            'State-of-the-art teaching-learning methods',
            'Industry collaboration and research opportunities',
            'Innovative and entrepreneurial outlook',
            'Quality education with modern facilities',
            'Global perspective and ethical values'
          ],
          syllabus: [
            {
              title: 'BE(CSE) 2023-25 Exam 2024-26',
              link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
            },
            {
              title: 'BE(CSE) 2024-26 Exam 2025-27',
              link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
            },
            {
              title: 'VI Sem BE(CSE) Syllabus Session 2021-23 Exam 2022-24',
              link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
            }
          ]
        },
        {
          name: 'Information Technology',
          description: 'Started in 2000 as a Self-Financed Scheme (SFS), the Information Technology program emphasizes information systems, data management, and enterprise solutions.',
          features: [
            'Information systems design',
            'Data analytics and visualization',
            'Cloud computing',
            'Enterprise architecture',
            'IT infrastructure management',
            'Industry-aligned curriculum',
            'Practical project experience',
            'Modern computing facilities',
            'Professional skill development',
            'Career-oriented training'
          ],
          syllabus: [
            {
              title: 'BE(IT) 2023-25 Exam 2024-26',
              link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
            },
            {
              title: 'BE(IT) 2024-26 Exam 2025-27',
              link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
            },
            {
              title: 'VI Sem BE(IT) Syllabus Session 2021-23 Exam 2022-24',
              link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
            }
          ]
        },
        {
          name: 'Artificial Intelligence & Data Science',
          description: 'A cutting-edge program focusing on AI, machine learning, and data science applications, designed to meet the growing demands of the industry and society.',
          features: [
            'Machine learning algorithms',
            'Deep learning and neural networks',
            'Big data analytics',
            'Natural language processing',
            'Computer vision',
            'Research-oriented curriculum',
            'Industry-standard tools and technologies',
            'Practical AI applications',
            'Data-driven decision making',
            'Emerging technology trends'
          ],
          syllabus: [
            {
              title: 'BE(ADS) 2023-25 Exam 2024-26',
              link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
            },
            {
              title: 'BE(ADS) 2024-26 Exam 2025-27',
              link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
            }
          ]
        }
      ]
    },
    {
      title: 'Master of Engineering (M.E.)',
      icon: <Groups sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Started in 2006, the M.E. program in Computer Science offers advanced studies focusing on specialized areas of computer science and research. The program is designed to create competent professionals and researchers in the field.',
      features: [
        '2-year specialized program',
        'Research-oriented curriculum',
        'Industry collaboration',
        'Advanced electives',
        'Thesis work',
        'State-of-the-art research facilities',
        'Expert faculty guidance',
        'Publication opportunities',
        'Industry partnerships',
        'Professional development'
      ],
      syllabus: [
        {
          title: 'ME(CSE) 2023-2024 Exam 2024-25',
          link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
        }
      ]
    },
    {
      title: 'Doctor of Philosophy (Ph.D.)',
      icon: <Science sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Started in 2009, the Ph.D. program in Computer Science emphasizes research and innovation. The program aims to create researchers who can contribute to the advancement of computer science and technology.',
      features: [
        'Research-focused program',
        'Publication opportunities',
        'International collaborations',
        'Teaching experience',
        'Industry partnerships',
        'Advanced research facilities',
        'Expert supervision',
        'Interdisciplinary research',
        'Conference participation',
        'Academic leadership'
      ]
    },
    {
      title: 'Master of Computer Applications (MCA)',
      icon: <Computer sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'The MCA program is designed to provide students with a solid foundation in computer application development, software engineering, and IT management. The curriculum emphasizes both theoretical knowledge and practical skills required for the IT industry.',
      features: [
        'Comprehensive curriculum covering programming, databases, and software engineering',
        'Focus on application development and project work',
        'Industry-oriented training and internships',
        'Modern computing labs and resources',
        'Professional skill development and career guidance'
      ],
      syllabus: [
        {
          title: 'MCA Syllabus Session 2022-23 Exam 2023-24 (Two Year Scheme)',
          link: 'https://www.mbm.ac.in/cse-scheme-syllabus'
        }
      ]
    }
  ];

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 },
      background: '#fff',
      color: '#1F2937',
      minHeight: '100vh'
    }}>
      <Container maxWidth="lg">
        <Typography variant="h3" align="center" gutterBottom sx={{ 
          mb: 8,
          fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.5rem' },
          position: 'relative',
          color: '#1F2937',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -16,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 80,
            height: 4,
            background: '#4B5563',
            borderRadius: 2
          }
        }}>
          Academic Programs
        </Typography>

        <Grid container spacing={4}>
          {programs.map((program, index) => (
            <Grid item xs={12} key={index}>
              <Card sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                borderRadius: 4,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    gap: 2
                  }}>
                    {program.icon}
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: 'primary.main'
                    }}>
                      {program.title}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {program.description}
                  </Typography>

                  {program.specializations ? (
                    <Box sx={{ mt: 4 }}>
                      {program.specializations.map((spec, specIndex) => (
                        <Accordion 
                          key={specIndex}
                          sx={{ 
                            mb: 2,
                            background: 'rgba(26,35,126,0.05)',
                            '&:before': { display: 'none' },
                            boxShadow: 'none',
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              background: 'rgba(26,35,126,0.1)',
                              '&:hover': {
                                background: 'rgba(26,35,126,0.15)',
                              }
                            }}
                          >
                            <Typography sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {spec.name}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <Typography variant="body1" color="text.secondary">
                                {spec.description}
                              </Typography>

                              <Box>
                                <Typography variant="h6" color="primary.main" gutterBottom>
                                  Program Features
                                </Typography>
                                <Grid container spacing={2}>
                                  {spec.features.map((feature, idx) => (
                                    <Grid item xs={12} sm={6} key={idx}>
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 1,
                                        color: 'text.secondary'
                                      }}>
                                        <Box sx={{ 
                                          width: 6, 
                                          height: 6, 
                                          borderRadius: '50%', 
                                          bgcolor: 'primary.main' 
                                        }} />
                                        <Typography variant="body2">
                                          {feature}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>

                              {spec.syllabus && (
                                <Box>
                                  <Typography variant="h6" color="primary.main" gutterBottom>
                                    Syllabus
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {spec.syllabus.map((item, idx) => (
                                      <Link
                                        key={idx}
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          color: 'primary.main',
                                          textDecoration: 'none',
                                          '&:hover': {
                                            textDecoration: 'underline'
                                          }
                                        }}
                                      >
                                        <Description fontSize="small" />
                                        <Typography variant="body2">
                                          {item.title}
                                        </Typography>
                                      </Link>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h6" color="primary.main" gutterBottom>
                        Program Features
                      </Typography>
                      <Grid container spacing={2}>
                        {program.features.map((feature, idx) => (
                          <Grid item xs={12} sm={6} key={idx}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              color: 'text.secondary'
                            }}>
                              <Box sx={{ 
                                width: 6, 
                                height: 6, 
                                borderRadius: '50%', 
                                bgcolor: 'primary.main' 
                              }} />
                              <Typography variant="body2">
                                {feature}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      {program.syllabus && (
                        <Box sx={{ mt: 4 }}>
                          <Typography variant="h6" color="primary.main" gutterBottom>
                            Syllabus
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {program.syllabus.map((item, idx) => (
                              <Link
                                key={idx}
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                <Description fontSize="small" />
                                <Typography variant="body2">
                                  {item.title}
                                </Typography>
                              </Link>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Study Material Section */}
        <Box sx={{ mt: { xs: 6, md: 10 }, mb: { xs: 4, md: 6 } }}>
          <Typography variant="h3" align="center" gutterBottom sx={{
            mb: { xs: 4, md: 6 },
            fontWeight: 800,
            fontSize: { xs: '1.8rem', sm: '2rem', md: '2.5rem' },
            color: 'primary.main',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 80,
              height: 4,
              background: 'linear-gradient(90deg, #1a237e 0%, #c62828 100%)',
              borderRadius: 2
            }
          }}>
            Study Material
          </Typography>
          <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
            {[
              { branch: 'CSE', color: '#1a237e' },
              { branch: 'IT', color: '#c62828' },
              { branch: 'AI&DS', color: '#00897b' }
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={item.branch}>
                <Card sx={{
                  background: 'rgba(255,255,255,0.97)',
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
                  p: { xs: 2, md: 3 },
                  textAlign: 'center',
                  borderTop: `6px solid ${item.color}`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 700, 
                      color: item.color, 
                      mb: { xs: 2, md: 3 },
                      fontSize: { xs: '1.3rem', md: '1.5rem' }
                    }}>
                      {item.branch}
                    </Typography>
                    <Grid container spacing={{ xs: 1, md: 2 }} justifyContent="center">
                      {[2, 3, 4].map(year => (
                        <Grid item key={year}>
                          <Button
                            variant="contained"
                            color="primary"
                            sx={{
                              minWidth: { xs: 90, sm: 100, md: 120 },
                              py: { xs: 1, md: 1.5 },
                              px: { xs: 2, md: 3 },
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              background: `linear-gradient(45deg, ${item.color} 60%, #fff 100%)`,
                              color: '#fff',
                              fontWeight: 600,
                              borderRadius: 2,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              '&:hover': {
                                background: `linear-gradient(45deg, #000 30%, ${item.color} 90%)`,
                                color: '#fff',
                              },
                            }}
                            href="#" // TODO: Replace with actual links
                          >
                            {year} Year
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Academics; 